import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { WifiOff, Wifi, Signal } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OfflineIndicatorProps {
  className?: string;
  variant?: 'badge' | 'alert' | 'minimal';
}

export function OfflineIndicator({ className, variant = 'badge' }: OfflineIndicatorProps) {
  const networkStatus = useNetworkStatus();

  if (networkStatus.isOnline && !networkStatus.isSlowConnection) {
    return null;
  }

  if (variant === 'minimal') {
    return (
      <div className={cn('flex items-center gap-1 text-xs text-muted-foreground', className)}>
        {!networkStatus.isOnline ? (
          <>
            <WifiOff className="h-3 w-3 text-destructive" />
            <span>Offline</span>
          </>
        ) : (
          <>
            <Signal className="h-3 w-3 text-orange-500" />
            <span>Slow connection</span>
          </>
        )}
      </div>
    );
  }

  if (variant === 'alert') {
    return (
      <Alert className={cn('border-orange-200 bg-orange-50 text-orange-900 dark:border-orange-800 dark:bg-orange-900 dark:text-orange-100', className)}>
        {!networkStatus.isOnline ? (
          <WifiOff className="h-4 w-4" />
        ) : (
          <Signal className="h-4 w-4" />
        )}
        <AlertDescription>
          {!networkStatus.isOnline 
            ? 'You are currently offline. Some features may not work properly.'
            : 'Your connection is slow. Operations may take longer than usual.'
          }
        </AlertDescription>
      </Alert>
    );
  }

  // Badge variant (default)
  return (
    <Badge 
      variant={!networkStatus.isOnline ? 'destructive' : 'secondary'}
      className={cn('flex items-center gap-1', className)}
    >
      {!networkStatus.isOnline ? (
        <>
          <WifiOff className="h-3 w-3" />
          Offline
        </>
      ) : (
        <>
          <Signal className="h-3 w-3" />
          Slow
        </>
      )}
    </Badge>
  );
}

// Connection status component for header/status bar
export function ConnectionStatus() {
  const networkStatus = useNetworkStatus();

  return (
    <div className="flex items-center gap-2">
      {networkStatus.isOnline ? (
        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <Wifi className="h-3 w-3" />
          <span>Online</span>
          {networkStatus.isSlowConnection && (
            <Badge variant="secondary" className="text-xs">
              Slow
            </Badge>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1 text-xs text-destructive">
          <WifiOff className="h-3 w-3" />
          <span>Offline</span>
        </div>
      )}
    </div>
  );
}